/**
 * LiveKit ("meditalk" provider) session adapter for the delivery video call UI.
 *
 * WHY AN ADAPTER: `VideoCall.jsx` was written directly against the OpenTok
 * (`OT.*`) surface — initSession / connect / initPublisher / publish / subscribe
 * / unpublish / disconnect, plus DOM containers the SDK injects a <video> into.
 * The Vonage account is dead, but the Vonage code path must keep working for any
 * call the backend still pins to `video_provider: "vonage"`. So instead of
 * rewriting the component, this presents the same small slice of that surface
 * backed by livekit-client, and the component branches once on the provider.
 *
 * WHICH PROVIDER: decided by the backend, never here. The call payload carries
 * `video_provider`, and MediTalk rows carry `livekit_url` with `application_id`
 * deliberately empty — so a stale cached bundle that predates this file fails
 * its `Boolean(application_id)` guard and reports "cannot connect" loudly rather
 * than silently dialling a dead Vonage session.
 *
 * FIELD REUSE: MediTalk credentials arrive in the Vonage-shaped fields —
 * `vonage_session_id` is the LiveKit room name (`oatrx-<uuid>`) and
 * `vonage_token` is a LiveKit JWT. That is a backend decision; this file just
 * consumes it.
 */

import {
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  createLocalVideoTrack,
} from 'livekit-client';

import {
  MEDITALK_AUDIO_CAPTURE_OPTIONS,
  getMeditalkPublishProfile,
} from '../../utils/meditalkPublisherQuality';

export const PROVIDER_VONAGE = 'vonage';
export const PROVIDER_MEDITALK = 'meditalk';

/**
 * Which provider a call belongs to. Read from the value the backend pinned when
 * the call row was created — never from a client-side setting, or the two
 * participants could disagree about which service to join.
 */
export const providerOf = (callData) =>
  (String(callData?.video_provider || '').trim().toLowerCase() === PROVIDER_MEDITALK
    ? PROVIDER_MEDITALK
    : PROVIDER_VONAGE);

export const isMeditalkCall = (callData) => providerOf(callData) === PROVIDER_MEDITALK;

/**
 * A remote peer vanishing is NOT the call ending. LiveKit fires
 * ParticipantDisconnected during ordinary reconnects, and drivers pass through
 * dead coverage constantly, so we wait this long for them to come back before
 * telling the component the other side left.
 */
const REMOTE_LEFT_GRACE_MS = 8000;

class LiveKitSessionAdapter {
  constructor({ roomName, url, role = 'driver' } = {}) {
    this.roomName = roomName || '';
    this.url = url || '';
    this.role = role;
    this.handlers = new Map();
    this.connected = false;
    this.destroyed = false;
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.localContainer = null;
    this.remoteContainer = null;
    this.attachedRemote = new Map();
    this.remoteLeftTimer = null;
    this.remoteLeftNotified = false;

    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      // Single layer, not the default three-layer 720p ladder: the extra layers
      // are pure upstream cost on a metered cellular link.
      publishDefaults: { videoSimulcastLayers: [] },
    });

    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      this.attachRemoteTrack(track, publication, participant);
      this.emit('streamCreated', { track, publication, participant });
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      this.detachRemoteTrack(track, publication);
      this.emit('streamDestroyed', { track, publication, participant });
    });

    this.room.on(RoomEvent.TrackMuted, (publication, participant) => {
      if (publication?.kind === Track.Kind.Video) {
        this.emit('videoDisabled', { publication, participant });
      }
    });

    this.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
      if (publication?.kind === Track.Kind.Video) {
        this.emit('videoEnabled', { publication, participant });
      }
    });

    this.room.on(RoomEvent.ParticipantConnected, () => {
      this.clearRemoteLeftTimer();
      this.emit('connectionCreated', {});
    });

    // Grace period, not an immediate hang-up. See REMOTE_LEFT_GRACE_MS.
    this.room.on(RoomEvent.ParticipantDisconnected, () => {
      this.scheduleRemoteLeftCheck();
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      this.clearRemoteLeftTimer();
      this.emit('reconnecting', {});
    });

    this.room.on(RoomEvent.Reconnected, () => {
      this.clearRemoteLeftTimer();
      this.emit('reconnected', {});
    });

    this.room.on(RoomEvent.Disconnected, () => {
      this.connected = false;
      this.emit('sessionDisconnected', {});
    });

    this.room.on(RoomEvent.MediaDevicesError, (error) => {
      this.emit('error', error);
    });
  }

  /* ---------------------------------------------------------------- events */

  on(event, handler) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event).add(handler);
    return this;
  }

  off(event, handler) {
    this.handlers.get(event)?.delete(handler);
    return this;
  }

  emit(event, payload) {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        console.warn(`[livekit] handler for "${event}" threw`, error);
      }
    });
  }

  /* ------------------------------------------------------------- lifecycle */

  /** Mirrors OpenTok's `session.connect(token, callback)`. */
  async connect(token, callback) {
    try {
      if (!this.url) {
        throw new Error('MediTalk call is missing livekit_url — backend did not supply one.');
      }
      if (!token) {
        throw new Error('MediTalk call is missing a LiveKit token.');
      }
      await this.room.connect(this.url, token);
      if (this.destroyed) {
        this.room.disconnect();
        return;
      }
      this.connected = true;
      this.emit('sessionConnected', {});
      callback?.(null);
    } catch (error) {
      callback?.(error);
      throw error;
    }
  }

  /**
   * Publish mic and camera.
   *
   * Mic and camera are attempted INDEPENDENTLY on purpose: a device with no
   * usable camera (or a camera the OS has handed to another app) must still get
   * audio rather than failing the whole publish and leaving a silent call.
   */
  async publish({ audioDeviceId, videoDeviceId, publishAudio = true, publishVideo = true } = {}) {
    const profile = getMeditalkPublishProfile(this.role);
    const failures = [];

    try {
      const audio = await createLocalAudioTrack({
        deviceId: audioDeviceId || undefined,
        ...MEDITALK_AUDIO_CAPTURE_OPTIONS,
      });
      if (this.destroyed) {
        audio.stop();
      } else {
        await this.room.localParticipant.publishTrack(audio, { dtx: true, red: true });
        this.localAudioTrack = audio;
        if (!publishAudio) await audio.mute();
      }
    } catch (error) {
      failures.push({ kind: 'audio', error });
      console.warn('[livekit] microphone unavailable, continuing without it', error);
      this.emit('audioUnavailable', error);
    }

    try {
      const video = await createLocalVideoTrack({
        deviceId: videoDeviceId || undefined,
        resolution: profile.resolution,
      });
      if (this.destroyed) {
        video.stop();
      } else {
        await this.room.localParticipant.publishTrack(video, {
          videoEncoding: {
            maxBitrate: profile.maxBitrate,
            maxFramerate: profile.maxFramerate,
          },
          degradationPreference: profile.degradationPreference,
          simulcast: false,
        });
        this.localVideoTrack = video;
        if (!publishVideo) await video.mute();
        this.attachLocalVideo();
      }
    } catch (error) {
      failures.push({ kind: 'video', error });
      console.warn('[livekit] camera unavailable, continuing audio-only', error);
      this.emit('videoUnavailable', error);
    }

    if (failures.length === 2) {
      const error = new Error('Neither microphone nor camera could be published.');
      error.causes = failures;
      throw error;
    }

    return { audioTrack: this.localAudioTrack, videoTrack: this.localVideoTrack, failures };
  }

  /* -------------------------------------------------------------- controls */

  async setAudioEnabled(enabled) {
    if (!this.localAudioTrack) {
      if (!enabled) return false;
      try {
        await this.publishAudioOnly();
        return Boolean(this.localAudioTrack);
      } catch (error) {
        console.warn('[livekit] unable to enable microphone', error);
        return false;
      }
    }
    try {
      if (enabled) await this.localAudioTrack.unmute();
      else await this.localAudioTrack.mute();
      return true;
    } catch (error) {
      console.warn('[livekit] unable to toggle microphone', error);
      return false;
    }
  }

  async setVideoEnabled(enabled, videoDeviceId) {
    if (!this.localVideoTrack) {
      if (!enabled) return false;
      try {
        await this.publishVideoOnly(videoDeviceId);
        return Boolean(this.localVideoTrack);
      } catch (error) {
        console.warn('[livekit] unable to enable camera', error);
        return false;
      }
    }
    try {
      if (enabled) await this.localVideoTrack.unmute();
      else await this.localVideoTrack.mute();
      return true;
    } catch (error) {
      console.warn('[livekit] unable to toggle camera', error);
      return false;
    }
  }

  async publishAudioOnly(audioDeviceId) {
    const audio = await createLocalAudioTrack({
      deviceId: audioDeviceId || undefined,
      ...MEDITALK_AUDIO_CAPTURE_OPTIONS,
    });
    if (this.destroyed) {
      audio.stop();
      return null;
    }
    await this.room.localParticipant.publishTrack(audio, { dtx: true, red: true });
    this.localAudioTrack = audio;
    return audio;
  }

  async publishVideoOnly(videoDeviceId) {
    const profile = getMeditalkPublishProfile(this.role);
    const video = await createLocalVideoTrack({
      deviceId: videoDeviceId || undefined,
      resolution: profile.resolution,
    });
    if (this.destroyed) {
      video.stop();
      return null;
    }
    await this.room.localParticipant.publishTrack(video, {
      videoEncoding: { maxBitrate: profile.maxBitrate, maxFramerate: profile.maxFramerate },
      degradationPreference: profile.degradationPreference,
      simulcast: false,
    });
    this.localVideoTrack = video;
    this.attachLocalVideo();
    return video;
  }

  /** Equivalent of `publisher.setVideoSource(deviceId)`. */
  async setVideoSource(deviceId) {
    if (!this.localVideoTrack) {
      await this.publishVideoOnly(deviceId);
      return deviceId;
    }
    const profile = getMeditalkPublishProfile(this.role);
    await this.localVideoTrack.restartTrack({
      deviceId: deviceId || undefined,
      resolution: profile.resolution,
    });
    this.attachLocalVideo();
    return deviceId;
  }

  /** Equivalent of `publisher.setAudioSource(deviceId)`. */
  async setAudioSource(deviceId) {
    if (!this.localAudioTrack) {
      await this.publishAudioOnly(deviceId);
      return deviceId;
    }
    await this.localAudioTrack.restartTrack({
      deviceId: deviceId || undefined,
      ...MEDITALK_AUDIO_CAPTURE_OPTIONS,
    });
    return deviceId;
  }

  /* ------------------------------------------------------------------- DOM */

  setLocalContainer(element) {
    this.localContainer = element || null;
    if (element) this.attachLocalVideo();
  }

  setRemoteContainer(element) {
    this.remoteContainer = element || null;
    if (!element) return;
    // Re-attach anything already subscribed (container can mount after connect).
    this.room?.remoteParticipants?.forEach((participant) => {
      participant.trackPublications?.forEach((publication) => {
        if (publication?.track) this.attachRemoteTrack(publication.track, publication, participant);
      });
    });
  }

  attachLocalVideo() {
    const container = this.localContainer;
    const track = this.localVideoTrack;
    if (!container || !track) return null;
    try {
      container.innerHTML = '';
      const element = track.attach();
      element.muted = true;
      element.playsInline = true;
      element.autoplay = true;
      element.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      container.appendChild(element);
      return element;
    } catch (error) {
      console.warn('[livekit] unable to attach local video', error);
      return null;
    }
  }

  attachRemoteTrack(track, publication, participant) {
    if (!track) return null;
    const key = publication?.trackSid || `${participant?.identity || 'remote'}-${track.kind}`;
    if (this.attachedRemote.has(key)) return this.attachedRemote.get(key);
    try {
      if (track.kind === Track.Kind.Audio) {
        // Audio is attached to a detached element so it plays regardless of the
        // visual container's mount state.
        const element = track.attach();
        element.autoplay = true;
        element.style.display = 'none';
        document.body.appendChild(element);
        this.attachedRemote.set(key, element);
        return element;
      }
      const container = this.remoteContainer;
      if (!container) return null;
      const element = track.attach();
      element.playsInline = true;
      element.autoplay = true;
      element.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      container.appendChild(element);
      this.attachedRemote.set(key, element);
      return element;
    } catch (error) {
      console.warn('[livekit] unable to attach remote track', error);
      return null;
    }
  }

  detachRemoteTrack(track, publication) {
    const key = publication?.trackSid;
    try {
      track?.detach()?.forEach((element) => element.remove?.());
    } catch (error) {
      /* already detached */
    }
    if (key) {
      this.attachedRemote.get(key)?.remove?.();
      this.attachedRemote.delete(key);
    }
  }

  /* ------------------------------------------------------- remote presence */

  clearRemoteLeftTimer() {
    if (this.remoteLeftTimer) {
      clearTimeout(this.remoteLeftTimer);
      this.remoteLeftTimer = null;
    }
  }

  scheduleRemoteLeftCheck() {
    if (this.destroyed || this.remoteLeftNotified) return;
    this.clearRemoteLeftTimer();
    this.remoteLeftTimer = setTimeout(() => {
      this.remoteLeftTimer = null;
      if (this.destroyed || this.remoteLeftNotified) return;
      const remaining = this.room?.remoteParticipants?.size ?? 0;
      if (remaining > 0) return;
      this.remoteLeftNotified = true;
      this.emit('remoteLeft', {});
    }, REMOTE_LEFT_GRACE_MS);
  }

  /* ------------------------------------------------------------- teardown */

  /** Mirrors OpenTok's `session.disconnect()`. */
  disconnect() {
    this.destroyed = true;
    this.clearRemoteLeftTimer();
    try {
      this.attachedRemote.forEach((element) => element?.remove?.());
      this.attachedRemote.clear();
    } catch (error) {
      /* ignore */
    }
    [this.localAudioTrack, this.localVideoTrack].forEach((track) => {
      try {
        track?.stop();
      } catch (error) {
        /* already stopped */
      }
    });
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    if (this.localContainer) this.localContainer.innerHTML = '';
    try {
      this.room.disconnect();
    } catch (error) {
      console.warn('[livekit] disconnect error', error);
    }
    this.connected = false;
    this.handlers.clear();
  }
}

/**
 * Build a MediTalk session. Returns null unless the call is actually a MediTalk
 * call, so the caller keeps its existing OpenTok path untouched.
 */
export const createLiveKitSession = ({ roomName, url, role = 'driver' } = {}) =>
  new LiveKitSessionAdapter({ roomName, url, role });

export default createLiveKitSession;
